import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../PermissionsProvider";
import { AgentService } from "../../services/AgentService";
import { AgentActionService } from "../../services/AgentActionService";
import { Agent } from "../../model_gen/agent_base";

import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Skeleton } from "../ui/skeleton";
import { MoreHorizontalIcon, TrashIcon, Ban, CheckCircle2, Copy, TableIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";


interface AgentCapabilitiesTableProps {
  agentId: number;
  onAddCapability?: () => void;
}

interface CombinedCapability {
  id: number;
  name: string;
  type: "action" | "subagent";
  description: string;
  status?: string;
  action_type?: string;
  application_name?: string;
  invocation_mode?: "foreground" | "background";
}

const AgentCapabilitiesTable: React.FC<AgentCapabilitiesTableProps> = ({ agentId, onAddCapability }) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [fullAgentTree, setFullAgentTree] = useState<Agent | null>(null);

  const [isLoading, setIsLoading] = useState(true);

  const [removeDialogOpen, setRemoveDialogOpen] = useState<string | null>(null);
  const [removingCapability, setRemovingCapability] = useState<CombinedCapability | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [rowStatusMap, setRowStatusMap] = useState<Record<string, "ENABLED" | "DISABLED" | "LOADING">>({});
  const [copyDialogOpen, setCopyDialogOpen] = useState<string | null>(null);
  const [copyingCapability, setCopyingCapability] = useState<CombinedCapability | null>(null);
  const [targetAgentIdInput, setTargetAgentIdInput] = useState<string>("");
  const [isCopying, setIsCopying] = useState(false);
  const [agentsForCopy, setAgentsForCopy] = useState<Agent[]>([]);
  const [isCopyListLoading, setIsCopyListLoading] = useState(false);
  const [copySearchTerm, setCopySearchTerm] = useState("");
  const filteredAgentsForCopy = useMemo(() => {
    const term = copySearchTerm.trim().toLowerCase();
    if (!term) return agentsForCopy;
    return agentsForCopy.filter((a) => {
      const app = (a.application_name || "").toLowerCase();
      const name = (a.agent_name || "").toLowerCase();
      return app.includes(term) || name.includes(term);
    });
  }, [agentsForCopy, copySearchTerm]);

  // Load capabilities data
  useEffect(() => {
    if (agentId && apiKey) {
      loadCapabilities();
    }
  }, [agentId, apiKey]);

  const loadCapabilities = () => {
    setIsLoading(true);

    // Load agent with detailed nested structure in a single call
    AgentService.getAgentDetailed(agentId, setIsLoading, apiKey).subscribe(
      (agent: Agent) => {
        setFullAgentTree(agent);

        setIsLoading(false);
      },
      (error: any) => {
        console.error("Error loading agent details:", error);
        setIsLoading(false);
      }
    );
  };





  const handleRemoveCapability = (capability: CombinedCapability) => {
    setRemovingCapability(capability);
    setRemoveDialogOpen(`${capability.type}-${capability.id}`);
  };

  const handleCopyCapability = (capability: CombinedCapability) => {
    setCopyingCapability(capability);
    setTargetAgentIdInput("");
    setCopyDialogOpen(`${capability.type}-${capability.id}`);
    // Load agents list on open (exclude current agent)
    setIsCopyListLoading(true);
    AgentService.getAgents((l: boolean) => setIsCopyListLoading(l), apiKey || "").subscribe(
      (resp: any) => {
        try {
          const list: Agent[] = (resp?.agents || resp || []) as Agent[];
          const filtered = (list || []).filter((a) => (a.id || 0) !== agentId);
          // Sort by application then agent name for ease of use
          filtered.sort((a, b) => {
            const an = (a.application_name || "") + "\u0000" + (a.agent_name || "");
            const bn = (b.application_name || "") + "\u0000" + (b.agent_name || "");
            return an.localeCompare(bn);
          });
          setAgentsForCopy(filtered);
        } catch (e) {
          console.error("Error processing agents list", e);
          setAgentsForCopy([]);
        } finally {
          setIsCopyListLoading(false);
        }
      },
      (error: any) => {
        console.error("Error loading agents list", error);
        setAgentsForCopy([]);
        setIsCopyListLoading(false);
      }
    );
  };

  const confirmRemoveCapability = async () => {
    if (!removingCapability || isRemoving || !apiKey) return;

    setIsRemoving(true);
    
    try {
      if (removingCapability.type === "action") {
        // For actions, delete the action completely
        await new Promise((resolve, reject) => {
          AgentActionService.deleteAction(removingCapability.id, () => {}, apiKey).subscribe(
            () => {
              console.log("Action removed successfully");
              resolve(void 0);
            },
            (error: any) => {
              console.error("Error removing action:", error);
              reject(error);
            }
          );
        });
      } else if (removingCapability.type === "subagent") {
        // For sub-agents, remove the relationship (doesn't delete the sub-agent itself)
        await new Promise((resolve, reject) => {
          AgentService.removeSubAgent(agentId, removingCapability.id, () => {}, apiKey).subscribe(
            () => {
              console.log("Sub-agent relationship removed successfully");
              resolve(void 0);
            },
            (error: any) => {
              console.error("Error removing sub-agent relationship:", error);
              reject(error);
            }
          );
        });
      }
      
      // Reload the capabilities to refresh the view and show the updated list
      loadCapabilities();
      
      // Close dialog and reset state
      setRemoveDialogOpen(null);
      setRemovingCapability(null);
    } catch (error) {
      console.error("Error removing capability:", error);
      // TODO: Show error message to user
    } finally {
      setIsRemoving(false);
    }
  };

  const toggleActionStatus = async (capability: CombinedCapability, nextStatus: "ENABLED" | "DISABLED") => {
    if (!apiKey) return;
    const key = `${capability.type}-${capability.id}`;
    setTogglingId(key);
    setRowStatusMap((prev) => ({ ...prev, [key]: "LOADING" }));
    try {
      await new Promise((resolve, reject) => {
        const req = nextStatus === "ENABLED"
          ? AgentService.enableAction(capability.id, () => {}, apiKey)
          : AgentService.disableAction(capability.id, () => {}, apiKey);
        req.subscribe(
          () => {
            resolve(void 0);
          },
          (error: any) => reject(error)
        );
      });
      // Optimistically update row status
      setRowStatusMap((prev) => ({ ...prev, [key]: nextStatus }));
    } catch (e) {
      console.error("Error toggling action status", e);
    } finally {
      setTogglingId(null);
    }
  };

  const disableSubAgentStatus = async (capability: CombinedCapability) => {
    if (!apiKey) return;
    const key = `${capability.type}-${capability.id}`;
    setTogglingId(key);
    setRowStatusMap((prev) => ({ ...prev, [key]: "LOADING" }));
    try {
      await new Promise((resolve, reject) => {
        AgentService.disableSubAgent(agentId, capability.id, () => {}, apiKey).subscribe(
          () => resolve(void 0),
          (error: any) => reject(error)
        );
      });
      // Optimistically set to DISABLED
      setRowStatusMap((prev) => ({ ...prev, [key]: "DISABLED" }));
    } catch (e) {
      console.error("Error disabling sub-agent relationship", e);
    } finally {
      setTogglingId(null);
    }
  };

  const enableSubAgentStatus = async (capability: CombinedCapability) => {
    if (!apiKey) return;
    const key = `${capability.type}-${capability.id}`;
    setTogglingId(key);
    setRowStatusMap((prev) => ({ ...prev, [key]: "LOADING" }));
    try {
      await new Promise((resolve, reject) => {
        AgentService.enableSubAgent(agentId, capability.id, () => {}, apiKey).subscribe(
          () => resolve(void 0),
          (error: any) => reject(error)
        );
      });
      // Optimistically set to ENABLED
      setRowStatusMap((prev) => ({ ...prev, [key]: "ENABLED" }));
    } catch (e) {
      console.error("Error enabling sub-agent relationship", e);
    } finally {
      setTogglingId(null);
    }
  };

  const confirmCopyCapability = async () => {
    if (!copyingCapability || copyingCapability.type !== "action" || isCopying || !apiKey) return;
    const parsed = parseInt(targetAgentIdInput, 10);
    if (!parsed || parsed <= 0) {
      console.error("Invalid target agent id");
      return;
    }
    setIsCopying(true);
    try {
      await new Promise((resolve, reject) => {
        AgentService.copyActionToAgent(copyingCapability.id, parsed, () => {}, apiKey).subscribe(
          () => resolve(void 0),
          (error: any) => reject(error)
        );
      });
      // Navigate to the target agent with the Capabilities tab open
      navigate(`/agent/${parsed}?tab=capabilities`);
    } catch (e) {
      console.error("Error copying action", e);
    } finally {
      setIsCopying(false);
    }
  };

  // Handle clicking on a capability in the table
  const handleCapabilityClick = (capability: CombinedCapability) => {
    if (capability.type === "action") {
      // Navigate to action overview/edit page
      navigate(`/agent/${agentId}/actions/${capability.id}`);
    } else if (capability.type === "subagent") {
      // Build parent chain by preserving existing chain and adding current agent ID
      const existingParents = searchParams.get("parents");
      
      let newParents;
      if (existingParents) {
        newParents = `${existingParents},${agentId}`;
      } else {
        newParents = agentId.toString();
      }
      
      // Navigate to sub-agent overview page with parent chain of IDs
      navigate(`/agent/${capability.id}?parents=${encodeURIComponent(newParents)}`);
    }
  };

  // Combined data for table view - flattened from nested structure
  const combinedTableData: CombinedCapability[] = useMemo(() => {
    if (!fullAgentTree) return [];
    
    const capabilities: CombinedCapability[] = [];
    
    // Add only direct actions from this agent (no recursion)
    (fullAgentTree.actions || []).forEach(action => {
      capabilities.push({
        id: action.id || 0,
        name: action.action_name,
        type: "action" as const,
        description: action.description,
        status: action.status,
        action_type: action.action_type,
        // @ts-ignore until model_gen is regenerated everywhere
        invocation_mode: (action as any).invocation_mode || "foreground",
      });
    });
    
    // Add only direct sub-agents (no recursion)
    (fullAgentTree.sub_agents || []).forEach(subAgent => {
      capabilities.push({
        id: subAgent.id || 0,
        name: subAgent.agent_name,
        type: "subagent" as const,
        description: subAgent.description || "",
        status: subAgent.status,
      });
    });
    
    return capabilities;
  }, [fullAgentTree]);

  const getTypeColor = (type: string) => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border";
    
    const typeClasses: Record<string, string> = {
      "api_call": "border-[#ffdb6a] text-yellow-700 dark:text-white bg-card [background-image:linear-gradient(rgba(255,219,106,0.1),rgba(255,219,106,0.1))] dark:[background-image:linear-gradient(rgba(255,219,106,0.2),rgba(255,219,106,0.2))]",
      "llm_call": "border-[#ffdb6a] text-yellow-700 dark:text-white bg-card [background-image:linear-gradient(rgba(255,219,106,0.1),rgba(255,219,106,0.1))] dark:[background-image:linear-gradient(rgba(255,219,106,0.2),rgba(255,219,106,0.2))]",
      "subagent": "border-[#ea5f40] text-[#ea5f40] dark:text-white bg-card [background-image:linear-gradient(rgba(234,95,64,0.1),rgba(234,95,64,0.1))] dark:[background-image:linear-gradient(rgba(234,95,64,0.2),rgba(234,95,64,0.2))]",
    };
    
    return `${baseClasses} ${typeClasses[type] || "bg-card border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"}`;
  };

  const getTypeLabel = (capability: CombinedCapability) => {
    if (capability.type === "action") {
      return capability.action_type === "api_call" ? "API Call" : "LLM Call";
    }
    return "Sub-Agent";
  };

  const getModeColor = (mode?: "foreground" | "background") => {
    const baseClasses = "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border";
    if (mode === "background") {
      return `${baseClasses} bg-card border-blue-300 text-blue-700 dark:text-blue-300 dark:border-blue-700`;
    }
    // default foreground style
    return `${baseClasses} bg-card border-green-300 text-green-700 dark:text-green-300 dark:border-green-700`;
  };

  const renderCapabilitiesTable = () => {
    if (isLoading && combinedTableData.length === 0) {
      return (
        <Card className="border-border">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border hover:bg-transparent">
                  <TableHead className="w-[25%]">Name</TableHead>
                  <TableHead className="w-[20%]">Type</TableHead>
                  <TableHead className="w-[35%]">Description</TableHead>
                  <TableHead className="w-[15%]">Status</TableHead>
                  <TableHead className="w-[5%]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...Array(5)].map((_, i) => (
                  <TableRow key={i} className="border-b border-border/50">
                    <TableCell className="py-4">
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-6 w-16 rounded-full" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell className="py-4">
                      <Skeleton className="h-4 w-4" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      );
    }

    if (combinedTableData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="space-y-4">
            <TableIcon className="w-12 h-12 text-muted-foreground mx-auto" />
            <div className="space-y-2">
              <h3 className="text-lg font-gc-med text-foreground">
                No Capabilities Yet
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Add your first capability to see them listed in a table format for easy management.
              </p>
            </div>
            <button 
              onClick={onAddCapability}
              className="text-maitai-green hover:underline text-sm font-gc-med"
            >
              Add First Capability
            </button>
          </div>
        </div>
      );
    }

    return (
      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-b border-border hover:bg-transparent">
                <TableHead className="w-[25%]">Name</TableHead>
                <TableHead className="w-[20%]">Type</TableHead>
                <TableHead className="w-[35%]">Description</TableHead>
                <TableHead className="w-[15%]">Status</TableHead>
                <TableHead className="w-[5%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {combinedTableData.map((capability) => (
                <TableRow
                  key={`${capability.type}-${capability.id}`}
                  className="cursor-pointer hover:bg-accent/20 transition-colors border-b border-border/50"
                  onClick={() => handleCapabilityClick(capability)}
                >
                  <TableCell className="py-4">
                    <div className="text-sm font-gc-reg text-foreground">
                      {capability.name}
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={getTypeColor(capability.type === "action" ? capability.action_type || "" : "subagent")}>
                        {getTypeLabel(capability)}
                      </span>
                      {capability.type === "action" && (
                        <span className={getModeColor(capability.invocation_mode)}>
                          {capability.invocation_mode === "background" ? "Background" : "Foreground"}
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="text-sm font-gc-reg text-foreground">
                      {capability.description 
                        ? Array.from(capability.description).length > 90 
                          ? Array.from(capability.description).slice(0, 90).join("") + "..."
                          : capability.description
                        : "No description provided"}
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    {(() => {
                      const key = `${capability.type}-${capability.id}`;
                      const effective = rowStatusMap[key] || capability.status || "ENABLED";
                      if (effective === "LOADING") {
                        return <Skeleton className="h-5 w-20 rounded-full" />;
                      }
                      return (
                        <span
                          className={cn(
                            "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                            effective === "DISABLED"
                              ? "bg-gray-100 text-gray-800 dark:bg-gray-800/40 dark:text-gray-300"
                              : "bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                          )}
                        >
                          {effective}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell className="py-4">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <MoreHorizontalIcon className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {capability.type === "action" && (
                          <DropdownMenuItem
                            className="cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyCapability(capability);
                            }}
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            Copy To Agent
                          </DropdownMenuItem>
                        )}
                        {capability.type === "action" && (() => {
                          const key = `${capability.type}-${capability.id}`;
                          const effective = rowStatusMap[key] || capability.status || "ENABLED";
                          const isDisabled = effective === "DISABLED";
                          return (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleActionStatus(capability, isDisabled ? "ENABLED" : "DISABLED");
                              }}
                              disabled={togglingId === `action-${capability.id}` || effective === "LOADING"}
                            >
                              {isDisabled ? (
                                <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                              ) : (
                                <Ban className="w-4 h-4 mr-2 text-muted-foreground" />
                              )}
                              {isDisabled ? "Enable" : "Disable"}
                            </DropdownMenuItem>
                          );
                        })()}
                        {capability.type === "subagent" && (() => {
                          const key = `${capability.type}-${capability.id}`;
                          const effective = rowStatusMap[key] || capability.status || "ENABLED";
                          const isDisabled = effective === "DISABLED";
                          return (
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isDisabled) {
                                  enableSubAgentStatus(capability);
                                } else {
                                  disableSubAgentStatus(capability);
                                }
                              }}
                              disabled={togglingId === `subagent-${capability.id}`}
                            >
                              {isDisabled ? (
                                <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                              ) : (
                                <Ban className="w-4 h-4 mr-2 text-muted-foreground" />
                              )}
                              {isDisabled ? "Enable" : "Disable"}
                            </DropdownMenuItem>
                          );
                        })()}
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCapability(capability);
                          }}
                        >
                          <TrashIcon className="w-4 h-4 mr-2" />
                          Remove
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4">
      {/* Table View */}
      <div>
        {renderCapabilitiesTable()}
      </div>



      {/* Remove Capability Confirmation Dialog */}
      <AlertDialog 
        open={removeDialogOpen !== null} 
        onOpenChange={(open) => {
          if (!open) {
            setRemoveDialogOpen(null);
            setRemovingCapability(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-gc-med">
              Remove Capability
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove "{removingCapability?.name}"?
              {removingCapability?.type === "action" 
                ? " This will permanently delete the action."
                : " This will remove the sub-agent relationship but won't delete the sub-agent itself."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                confirmRemoveCapability();
              }}
              disabled={isRemoving}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isRemoving ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Copy Capability Dialog */}
      <AlertDialog 
        open={copyDialogOpen !== null}
        onOpenChange={(open) => {
          if (!open) {
            setCopyDialogOpen(null);
            setCopyingCapability(null);
            setAgentsForCopy([]);
            setTargetAgentIdInput("");
            setCopySearchTerm("");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-gc-med">
              Copy Action To Agent
            </AlertDialogTitle>
            <AlertDialogDescription>
              Select the target agent to copy "{copyingCapability?.name}" to.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2">
            {isCopyListLoading ? (
              <div className="text-sm text-muted-foreground">Loading agents...</div>
            ) : (
              <div>
                <input
                  type="text"
                  className="w-full border rounded px-3 py-2 bg-background text-foreground mb-2"
                  placeholder="Search by application or agent name"
                  value={copySearchTerm}
                  onChange={(e) => setCopySearchTerm(e.target.value)}
                />
                <div className="max-h-64 overflow-auto border rounded">
                  {filteredAgentsForCopy.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-muted-foreground">No agents found</div>
                  ) : (
                    filteredAgentsForCopy.map((a) => {
                      const idStr = String(a.id || "");
                      const selected = targetAgentIdInput === idStr;
                      return (
                        <div
                          key={a.id}
                          role="button"
                          className={cn(
                            "px-3 py-2 text-sm cursor-pointer border-b last:border-b-0",
                            selected ? "bg-accent/30" : "hover:bg-accent/20"
                          )}
                          onClick={() => setTargetAgentIdInput(idStr)}
                        >
                          <div className="font-gc-reg text-foreground">{a.agent_name}</div>
                          {a.application_name && (
                            <div className="text-xs text-muted-foreground">{a.application_name}</div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCopying}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.stopPropagation();
                confirmCopyCapability();
              }}
              disabled={isCopying || !targetAgentIdInput}
            >
              {isCopying ? "Copying..." : "Copy"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AgentCapabilitiesTable; 